import { Component, Inject, LOCALE_ID, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { UserRole } from "src/model";
import { Location } from '@angular/common';

import { ScannerState } from 'src/store/scanner/scanner.state';
import { LineController, ProductionBatch, Scan, ScannerStatus, ScanStatus } from "../model";
import { combineLatest, concat, distinctUntilChanged, filter, Observable, Subject, tap } from "rxjs";
import { BadBucketPanelService } from "./components/dlg-bad-bucket-position/bad-bucket-panel.service";
import { ProductionBatchState } from "../store/batch/batch.state";
import { ErrorState } from "../store/error/error.state";
import { AuthenticationState } from "../store/auth/auth.state";
import { BackendService } from "../services/backend";
import { NavigationEnd, Router } from "@angular/router";
import { Settings } from "luxon";
import { isBadBucketScan } from "./components/failure-reason/failure-reason-messages";
import { PoAttachDialogComponent } from "./components/po-attach-dialog/po-attach-dialog.component";
import { OperatorReasonDialogComponent } from "./components/operator-reason-dialog/operator-reason-dialog.component";
import { NzModalRef, NzModalService } from "ng-zorro-antd/modal";

/** DEV-294: what the header light widget shows about the camera light level. */
export interface CameraLightLevelView {
  text: string;
  tooltip: string;
  /** The level is known and differs from the expected one. */
  mismatch: boolean;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  ScannerStatus = ScannerStatus;
  ScanStatus = ScanStatus;
  UserRole = UserRole;

  // Not static — this element sits inside the `@else if (isReady)` branch, which isn't in the
  // DOM yet at ngOnInit time.
  @ViewChild('attachDialog') attachD!: PoAttachDialogComponent;

  /** Reason popup for the header Resume (DEV-282); the scan dialogs' Eject / Continue use their own. */
  @ViewChild('continueReasonDialog') continueReasonDialog?: OperatorReasonDialogComponent;

  isMenuVisible = false;
  failedScan$!: Observable<Scan | null>
  isReady = false;
  isWelcomePage = false;
  cameraLightSwitchConfirmVisible = false;
  _pendingLightOn = false;

  /** DEV-294: the light level shown on the header light widget; null hides it (no light-level controller). */
  lightLevel: CameraLightLevelView | null = null;
  /** DEV-294: the "actual/expected" mismatch already warned about, so the backend's check before every scan pops up only once. */
  private warnedLightLevel: string | null = null;
  private lightLevelWarning: NzModalRef | null = null;

  showScanDialog = new Subject<boolean>();

  private destroyBadBucketPanel: (() => void) | null = null;

  constructor(public location: Location,
    public state: ScannerState,
    public batchState: ProductionBatchState,
    public errorState: ErrorState,
    public authState: AuthenticationState,
    public backend: BackendService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private badBucketPanel: BadBucketPanelService,
    private modal: NzModalService,
    @Inject(LOCALE_ID) public locale: string) {
  }

  line: LineController = {
    machineIsOn: false,
    lineIsOn: false,
    bucketIsInTheScanner: false,
    continue: false,
    eject: false,
    raiseAlarm: false,
    lightOn: false
  };

  ngOnInit(): void {
    // this.state.errorMessage$.pipe(
    //   tap(m => m && this.notification.error("Error", m, {
    //     nzDuration: 60000
    //   }))
    // ).subscribe()
    Settings.defaultLocale = this.locale;

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e) => {
      this.isWelcomePage = (e as NavigationEnd).urlAfterRedirects.startsWith('/welcome');
      this.cdr.detectChanges();
    });

    this.failedScan$ = combineLatest([
      this.showScanDialog,
      this.state.failedScan$.pipe(
        filter(_ => this.authState.hasAnyRole(UserRole.Operator, UserRole.Admin)),
        distinctUntilChanged((a, b) => a === b, s => s?.id),
        tap(v => v && this.showScanDialog.next(true))
      )],
      (t, s) => t ? s : null
    );

    this.failedScan$.subscribe(scan => {
      const isBadBucket = scan && isBadBucketScan(scan);
      console.log('AppComponent.failedScan$: received', { scanId: scan?.id, seq: scan?.sequenceInBatch, poName: scan?.productionBatch?.poName, failureReason: scan?.failureReason, isBadBucket, hasOpenPanel: !!this.destroyBadBucketPanel });

      if (scan && !this.router.url.startsWith(`/pos/${scan.productionBatchId}`)) {
        console.log('AppComponent.failedScan$: navigating to PO page for failed scan\'s batch', { productionBatchId: scan.productionBatchId });
        this.router.navigate(['/pos', scan.productionBatchId]);
      }

      if (isBadBucket && !this.destroyBadBucketPanel) {
        const batch = this.batchState.selectOne(scan.productionBatchId);
        console.log('AppComponent.failedScan$: opening bad-bucket panel — bucket was not in position, needs operator decision', { scanId: scan.id, seq: scan.sequenceInBatch, poName: batch?.poName });
        this.destroyBadBucketPanel = this.badBucketPanel.open({ scan, batch, isInViewMode: false }, (_scan) => {
          console.log('AppComponent.failedScan$: bad-bucket panel closed — hiding scan dialog so scanner can proceed');
          // if (_scan != null) {
          //   var newScanState = _scan.state;
          //   this.state.setManualInspectionResult(newScanState).subscribe({
          //     next: (scanner) => console.log('AppComponent.state.setManualInspectionResult{newScanState}:', { newScanState }),
          //     error: (err) => console.error('AppComponent.state.setManualInspectionResult() error', err)
          //   });
          // }
          this.destroyBadBucketPanel = null;
          this.showScanDialog.next(false);
        });
      } else if (isBadBucket && this.destroyBadBucketPanel) {
        console.log('AppComponent.failedScan$: bad-bucket panel already open — skipping duplicate open');
      } else if (!isBadBucket && this.destroyBadBucketPanel) {
        console.log('AppComponent.failedScan$: closing bad-bucket panel — scan is no longer a bad-bucket failure (scanner state changed)');
        this.destroyBadBucketPanel();
        this.destroyBadBucketPanel = null;
      }
    });

    const lineStatus$ = concat(
      this.backend.getLineStatus(),
      this.backend.getLineUpdates()
    );

    lineStatus$.subscribe(l => {
      this.applyLine(l);
      this.cdr.detectChanges();
    });

    // App-wide: pop the attach dialog for a newly-arrived (FLink) Pending batch regardless of
    // which page the user is on — this dialog lives here (not on a routed page) so the Angular
    // Router destroying/recreating pages doesn't drop the subscription.
    this.batchState.lastPending().pipe(
      filter(_ => this.authState.hasAnyRole(UserRole.Operator, UserRole.Admin))
    ).subscribe(b => {
      if (b)
        this.attachD.show(b);
      else
        this.attachD.cancel();
    });

    this.isReady = true;
  }


  /** Template helper: returns true for bad-bucket scans (DL-011 exact-match). */
  protected isBadBucket(scan: Scan): boolean {
    return isBadBucketScan(scan);
  }

  /** `reason`: the Operator's reason for ejecting without changing the status (DEV-282), from the dialog's popup. */
  onFailedBucketDialogDecision(decision: string, reason: string | null = null) {
    console.log('AppComponent.onFailedBucketDialogDecision() called', { decision, reason });
    if (decision == 'Eject') {
      this.state.resume(reason)
    } else {
      this.showScanDialog.next(false)
    }
  }

  /** `reason`: the Operator's reason for continuing without changing the status (DEV-282), from the dialog's popup. */
  onEjectedBucketDialogDecision(decision: string, reason: string | null = null) {
    if (decision == 'Continue') {
      this.state.resume(reason)
    } else {
      this.showScanDialog.next(false)
    }
  }

  /**
   * Header Resume. While the scanner is stopped on a bucket (Deciding / Ejected), resuming moves the line
   * on and keeps that bucket's status, so an Operator is asked for a reason first — the same rule as the
   * dialogs' Eject / Continue (DEV-282). Otherwise it resumes straight away, as before.
   */
  resumeLine(): void {
    this.state.continueNeedsOperatorReason().subscribe(needsReason => {
      if (needsReason) {
        this.continueReasonDialog?.open();
      } else {
        this.state.resume();
      }
    });
  }

  /** The Operator picked a reason in the header-Resume popup. Cancel does nothing: the line stays stopped. */
  onContinueReasonSelected(reason: string): void {
    this.state.resume(reason);
  }

  newBatch(quantity: number) {

  }

  attach([subject, to]: [string, ProductionBatch]) {
    this.batchState.attach(subject, to.id);
  }

  protected readonly window = window;

  switchLocale(locale: string): void {
    document.cookie = `locale=${locale}; path=/; max-age=31536000; SameSite=Lax`;
    window.location.href = '/' + locale + '/';
  }

  closeError(id: string) {
    this.errorState.dismiss(id)
  }

  roleColor(role: string): string {
    if (role === UserRole.Admin) return 'purple';
    if (role === UserRole.Operator) return 'success';
    if (role === UserRole.Guest) return 'default';
    return 'blue';
  }

  requestSetCameraLight(state: boolean): void {
    this._pendingLightOn = state;
    console.log(`[AppComponent] Camera light button clicked — requesting LightOn=${state}`);
    this.cameraLightSwitchConfirmVisible = true;
  }

  confirmSetCameraLight(): void {
    this.cameraLightSwitchConfirmVisible = false;
    const newState = this._pendingLightOn;
    console.log(`[AppComponent] User confirmed — sending LightOn=${newState} to backend`);
    this.backend.setLine({ lightOn: newState }).subscribe({
      next: (result) => {
        console.log(`[AppComponent] LightOn=${newState} confirmed by backend`, result);
        this.applyLine(result);
        this.cdr.detectChanges();
      },
      error: (err) => console.error(`[AppComponent] Failed to set LightOn=${newState}`, err),
    });
  }

  /** Takes a line state from the backend (GET, PATCH reply or SSE push). */
  applyLine(l: LineController): void {
    this.line = l;
    this.lightLevel = this.describeLightLevel(l);
    this.warnOnLightLevelMismatch(l);
  }

  private describeLightLevel(l: LineController): CameraLightLevelView | null {
    const level = l.lightIntensity;
    const expected = l.expectedLightIntensity;
    if (level != null) {
      return {
        text: `${level}`,
        mismatch: expected != null && level !== expected,
        tooltip: expected == null
          ? $localize`:@@cameraLightLevelTooltip:Camera light level ${level}:level:`
          : $localize`:@@cameraLightLevelExpectedTooltip:Camera light level ${level}:level: (expected ${expected}:expected:)`,
      };
    }
    if (l.lightIntensityError) {
      return {
        text: '?',
        mismatch: false,
        tooltip: $localize`:@@cameraLightLevelUnknownTooltip:Camera light level unknown: ${l.lightIntensityError}:error:`,
      };
    }
    return null;
  }

  /**
   * DEV-294: pops up a warning to Operators/Admins when the light level differs from the expected one.
   * Once per mismatch — again only when the level changes to another wrong value, or goes wrong again
   * after being right. The popup closes by itself once the level is right. An unknown level (failed
   * read) neither warns nor resets, so a flaky controller does not repeat the popup.
   */
  private warnOnLightLevelMismatch(l: LineController): void {
    const level = l.lightIntensity;
    const expected = l.expectedLightIntensity;
    if (level == null)
      return;

    if (expected == null || level === expected) {
      this.warnedLightLevel = null;
      this.lightLevelWarning?.close();
      this.lightLevelWarning = null;
      return;
    }

    const key = `${level}/${expected}`;
    if (key === this.warnedLightLevel || !this.authState.hasAnyRole(UserRole.Operator, UserRole.Admin))
      return;

    console.warn(`[AppComponent] Camera light level ${level} differs from the expected ${expected} — warning the operator`);
    this.warnedLightLevel = key;
    this.lightLevelWarning?.close();
    this.lightLevelWarning = this.modal.warning({
      nzTitle: $localize`:@@cameraLightLevelWarningTitle:Camera light level is wrong`,
      nzContent: $localize`:@@cameraLightLevelWarningContent:The camera light is at level ${level}:level:, but this station expects ${expected}:expected:. Colour results depend on the light, so they may be wrong until the level is corrected.`,
    });
  }

  calibrate() {
    this.backend.getCalibration().subscribe(s =>
      this.router.navigate(['/', 'pos', s.productionBatchId, 'scans', s.id])
    )
  }
}
